
import React, { useState, useEffect, useRef } from 'react';
import { ArrowLeftIcon, AddIcon, DownArrowIcon } from './Icons';
import { apiService } from '../../services/apiService';
import { armReturnAfterAuth, clearReturnAfterAuth, ssoRedirectUrls, updateReturnAfterAuth, withNext } from '../../utils/returnAfterAuth';
import { useSignIn, useClerk, useUser, useSession } from '@clerk/clerk-react';
import { trackClick } from '../../utils/analytics';
import { friendlyAuthError } from '../../utils/authErrors';
import { InlineAlert } from '../../components/InlineAlert';

// TypeScript declaration for dataLayer
declare global {
    interface Window {
        dataLayer?: any[];
    }
}

// GTM Event Tracking Helper
const trackGTMEvent = (eventName: string, eventData?: Record<string, any>) => {
    if (typeof window !== 'undefined' && window.dataLayer) {
        window.dataLayer.push({
            event: eventName,
            ...eventData
        });
    }
};

// Feature Flag: Enable/Disable LinkedIn OAuth button
// Set to true to show "Sign Up with LinkedIn" button, false to hide it.
// Hidden for now — the handler, the Clerk oauth_linkedin_oidc strategy and the
// OAuth callback in index.tsx are all left wired, so flipping this back to true
// restores the flow with no other change.
const ENABLE_LINKEDIN_AUTH = false;

// Feature Flag: Enable/Disable Google OAuth button
const ENABLE_GOOGLE_AUTH = true;

interface SignUpModalProps {
    onClose: () => void;
    onSignupSuccess?: (email: string) => void;
    /** True while OAuth signup is talking to the API (show full-page loader on landing). */
    onOAuthSignupPhaseChange?: (busy: boolean) => void;
    /** The parent navigates after onSignupSuccess (the public tech pages:
     *  onboarding, then back to the page) — no reload of our own. */
    skipReloadOnSuccess?: boolean;
    /** Opened by a public tech / university page (PublicShell), or resuming
     *  a Google sign-up that started there: send the visitor back to that page
     *  after auth (utils/returnAfterAuth). Anywhere else any pending page is
     *  forgotten, so an old one cannot hijack this sign-up. */
    returnAfterAuth?: boolean;
}

// Countries list with priority countries at top
const COUNTRIES = [
    { value: 'India', label: 'India', isPriority: true },
    { value: 'United States', label: 'United States', isPriority: true },
    { value: 'United Kingdom', label: 'United Kingdom', isPriority: true },
    { value: 'Germany', label: 'Germany', isPriority: true },
    { value: 'Malaysia', label: 'Malaysia', isPriority: true },
    { value: 'separator', label: '──────────', isPriority: false },
    { value: 'Afghanistan', label: 'Afghanistan', isPriority: false },
    { value: 'Albania', label: 'Albania', isPriority: false },
    { value: 'Algeria', label: 'Algeria', isPriority: false },
    { value: 'Andorra', label: 'Andorra', isPriority: false },
    { value: 'Angola', label: 'Angola', isPriority: false },
    { value: 'Argentina', label: 'Argentina', isPriority: false },
    { value: 'Armenia', label: 'Armenia', isPriority: false },
    { value: 'Australia', label: 'Australia', isPriority: false },
    { value: 'Austria', label: 'Austria', isPriority: false },
    { value: 'Azerbaijan', label: 'Azerbaijan', isPriority: false },
    { value: 'Bahamas', label: 'Bahamas', isPriority: false },
    { value: 'Bahrain', label: 'Bahrain', isPriority: false },
    { value: 'Bangladesh', label: 'Bangladesh', isPriority: false },
    { value: 'Barbados', label: 'Barbados', isPriority: false },
    { value: 'Belarus', label: 'Belarus', isPriority: false },
    { value: 'Belgium', label: 'Belgium', isPriority: false },
    { value: 'Belize', label: 'Belize', isPriority: false },
    { value: 'Benin', label: 'Benin', isPriority: false },
    { value: 'Bhutan', label: 'Bhutan', isPriority: false },
    { value: 'Bolivia', label: 'Bolivia', isPriority: false },
    { value: 'Bosnia and Herzegovina', label: 'Bosnia and Herzegovina', isPriority: false },
    { value: 'Botswana', label: 'Botswana', isPriority: false },
    { value: 'Brazil', label: 'Brazil', isPriority: false },
    { value: 'Brunei', label: 'Brunei', isPriority: false },
    { value: 'Bulgaria', label: 'Bulgaria', isPriority: false },
    { value: 'Burkina Faso', label: 'Burkina Faso', isPriority: false },
    { value: 'Burundi', label: 'Burundi', isPriority: false },
    { value: 'Cambodia', label: 'Cambodia', isPriority: false },
    { value: 'Cameroon', label: 'Cameroon', isPriority: false },
    { value: 'Canada', label: 'Canada', isPriority: false },
    { value: 'Cape Verde', label: 'Cape Verde', isPriority: false },
    { value: 'Central African Republic', label: 'Central African Republic', isPriority: false },
    { value: 'Chad', label: 'Chad', isPriority: false },
    { value: 'Chile', label: 'Chile', isPriority: false },
    { value: 'China', label: 'China', isPriority: false },
    { value: 'Colombia', label: 'Colombia', isPriority: false },
    { value: 'Comoros', label: 'Comoros', isPriority: false },
    { value: 'Congo', label: 'Congo', isPriority: false },
    { value: 'Costa Rica', label: 'Costa Rica', isPriority: false },
    { value: 'Croatia', label: 'Croatia', isPriority: false },
    { value: 'Cuba', label: 'Cuba', isPriority: false },
    { value: 'Cyprus', label: 'Cyprus', isPriority: false },
    { value: 'Czech Republic', label: 'Czech Republic', isPriority: false },
    { value: 'Denmark', label: 'Denmark', isPriority: false },
    { value: 'Djibouti', label: 'Djibouti', isPriority: false },
    { value: 'Dominica', label: 'Dominica', isPriority: false },
    { value: 'Dominican Republic', label: 'Dominican Republic', isPriority: false },
    { value: 'East Timor', label: 'East Timor', isPriority: false },
    { value: 'Ecuador', label: 'Ecuador', isPriority: false },
    { value: 'Egypt', label: 'Egypt', isPriority: false },
    { value: 'El Salvador', label: 'El Salvador', isPriority: false },
    { value: 'Equatorial Guinea', label: 'Equatorial Guinea', isPriority: false },
    { value: 'Eritrea', label: 'Eritrea', isPriority: false },
    { value: 'Estonia', label: 'Estonia', isPriority: false },
    { value: 'Ethiopia', label: 'Ethiopia', isPriority: false },
    { value: 'Fiji', label: 'Fiji', isPriority: false },
    { value: 'Finland', label: 'Finland', isPriority: false },
    { value: 'France', label: 'France', isPriority: false },
    { value: 'Gabon', label: 'Gabon', isPriority: false },
    { value: 'Gambia', label: 'Gambia', isPriority: false },
    { value: 'Georgia', label: 'Georgia', isPriority: false },
    { value: 'Ghana', label: 'Ghana', isPriority: false },
    { value: 'Greece', label: 'Greece', isPriority: false },
    { value: 'Grenada', label: 'Grenada', isPriority: false },
    { value: 'Guatemala', label: 'Guatemala', isPriority: false },
    { value: 'Guinea', label: 'Guinea', isPriority: false },
    { value: 'Guinea-Bissau', label: 'Guinea-Bissau', isPriority: false },
    { value: 'Guyana', label: 'Guyana', isPriority: false },
    { value: 'Haiti', label: 'Haiti', isPriority: false },
    { value: 'Honduras', label: 'Honduras', isPriority: false },
    { value: 'Hong Kong', label: 'Hong Kong', isPriority: false },
    { value: 'Hungary', label: 'Hungary', isPriority: false },
    { value: 'Iceland', label: 'Iceland', isPriority: false },
    { value: 'Indonesia', label: 'Indonesia', isPriority: false },
    { value: 'Iran', label: 'Iran', isPriority: false },
    { value: 'Iraq', label: 'Iraq', isPriority: false },
    { value: 'Ireland', label: 'Ireland', isPriority: false },
    { value: 'Israel', label: 'Israel', isPriority: false },
    { value: 'Italy', label: 'Italy', isPriority: false },
    { value: 'Jamaica', label: 'Jamaica', isPriority: false },
    { value: 'Japan', label: 'Japan', isPriority: false },
    { value: 'Jordan', label: 'Jordan', isPriority: false },
    { value: 'Kazakhstan', label: 'Kazakhstan', isPriority: false },
    { value: 'Kenya', label: 'Kenya', isPriority: false },
    { value: 'Kiribati', label: 'Kiribati', isPriority: false },
    { value: 'North Korea', label: 'North Korea', isPriority: false },
    { value: 'South Korea', label: 'South Korea', isPriority: false },
    { value: 'Kuwait', label: 'Kuwait', isPriority: false },
    { value: 'Kyrgyzstan', label: 'Kyrgyzstan', isPriority: false },
    { value: 'Laos', label: 'Laos', isPriority: false },
    { value: 'Latvia', label: 'Latvia', isPriority: false },
    { value: 'Lebanon', label: 'Lebanon', isPriority: false },
    { value: 'Lesotho', label: 'Lesotho', isPriority: false },
    { value: 'Liberia', label: 'Liberia', isPriority: false },
    { value: 'Libya', label: 'Libya', isPriority: false },
    { value: 'Liechtenstein', label: 'Liechtenstein', isPriority: false },
    { value: 'Lithuania', label: 'Lithuania', isPriority: false },
    { value: 'Luxembourg', label: 'Luxembourg', isPriority: false },
    { value: 'Madagascar', label: 'Madagascar', isPriority: false },
    { value: 'Malawi', label: 'Malawi', isPriority: false },
    { value: 'Maldives', label: 'Maldives', isPriority: false },
    { value: 'Mali', label: 'Mali', isPriority: false },
    { value: 'Malta', label: 'Malta', isPriority: false },
    { value: 'Marshall Islands', label: 'Marshall Islands', isPriority: false },
    { value: 'Mauritania', label: 'Mauritania', isPriority: false },
    { value: 'Mauritius', label: 'Mauritius', isPriority: false },
    { value: 'Mexico', label: 'Mexico', isPriority: false },
    { value: 'Micronesia', label: 'Micronesia', isPriority: false },
    { value: 'Moldova', label: 'Moldova', isPriority: false },
    { value: 'Monaco', label: 'Monaco', isPriority: false },
    { value: 'Mongolia', label: 'Mongolia', isPriority: false },
    { value: 'Montenegro', label: 'Montenegro', isPriority: false },
    { value: 'Morocco', label: 'Morocco', isPriority: false },
    { value: 'Mozambique', label: 'Mozambique', isPriority: false },
    { value: 'Myanmar', label: 'Myanmar', isPriority: false },
    { value: 'Namibia', label: 'Namibia', isPriority: false },
    { value: 'Nauru', label: 'Nauru', isPriority: false },
    { value: 'Nepal', label: 'Nepal', isPriority: false },
    { value: 'Netherlands', label: 'Netherlands', isPriority: false },
    { value: 'New Zealand', label: 'New Zealand', isPriority: false },
    { value: 'Nicaragua', label: 'Nicaragua', isPriority: false },
    { value: 'Niger', label: 'Niger', isPriority: false },
    { value: 'Nigeria', label: 'Nigeria', isPriority: false },
    { value: 'Norway', label: 'Norway', isPriority: false },
    { value: 'Oman', label: 'Oman', isPriority: false },
    { value: 'Pakistan', label: 'Pakistan', isPriority: false },
    { value: 'Palau', label: 'Palau', isPriority: false },
    { value: 'Panama', label: 'Panama', isPriority: false },
    { value: 'Papua New Guinea', label: 'Papua New Guinea', isPriority: false },
    { value: 'Paraguay', label: 'Paraguay', isPriority: false },
    { value: 'Peru', label: 'Peru', isPriority: false },
    { value: 'Philippines', label: 'Philippines', isPriority: false },
    { value: 'Poland', label: 'Poland', isPriority: false },
    { value: 'Portugal', label: 'Portugal', isPriority: false },
    { value: 'Qatar', label: 'Qatar', isPriority: false },
    { value: 'Romania', label: 'Romania', isPriority: false },
    { value: 'Russia', label: 'Russia', isPriority: false },
    { value: 'Rwanda', label: 'Rwanda', isPriority: false },
    { value: 'Saint Kitts and Nevis', label: 'Saint Kitts and Nevis', isPriority: false },
    { value: 'Saint Lucia', label: 'Saint Lucia', isPriority: false },
    { value: 'Saint Vincent and the Grenadines', label: 'Saint Vincent and the Grenadines', isPriority: false },
    { value: 'Samoa', label: 'Samoa', isPriority: false },
    { value: 'San Marino', label: 'San Marino', isPriority: false },
    { value: 'Sao Tome and Principe', label: 'Sao Tome and Principe', isPriority: false },
    { value: 'Saudi Arabia', label: 'Saudi Arabia', isPriority: false },
    { value: 'Senegal', label: 'Senegal', isPriority: false },
    { value: 'Serbia', label: 'Serbia', isPriority: false },
    { value: 'Seychelles', label: 'Seychelles', isPriority: false },
    { value: 'Sierra Leone', label: 'Sierra Leone', isPriority: false },
    { value: 'Singapore', label: 'Singapore', isPriority: false },
    { value: 'Slovakia', label: 'Slovakia', isPriority: false },
    { value: 'Slovenia', label: 'Slovenia', isPriority: false },
    { value: 'Solomon Islands', label: 'Solomon Islands', isPriority: false },
    { value: 'Somalia', label: 'Somalia', isPriority: false },
    { value: 'South Africa', label: 'South Africa', isPriority: false },
    { value: 'South Sudan', label: 'South Sudan', isPriority: false },
    { value: 'Spain', label: 'Spain', isPriority: false },
    { value: 'Sri Lanka', label: 'Sri Lanka', isPriority: false },
    { value: 'Sudan', label: 'Sudan', isPriority: false },
    { value: 'Suriname', label: 'Suriname', isPriority: false },
    { value: 'Swaziland', label: 'Swaziland', isPriority: false },
    { value: 'Sweden', label: 'Sweden', isPriority: false },
    { value: 'Switzerland', label: 'Switzerland', isPriority: false },
    { value: 'Syria', label: 'Syria', isPriority: false },
    { value: 'Taiwan', label: 'Taiwan', isPriority: false },
    { value: 'Tajikistan', label: 'Tajikistan', isPriority: false },
    { value: 'Tanzania', label: 'Tanzania', isPriority: false },
    { value: 'Thailand', label: 'Thailand', isPriority: false },
    { value: 'Togo', label: 'Togo', isPriority: false },
    { value: 'Tonga', label: 'Tonga', isPriority: false },
    { value: 'Trinidad and Tobago', label: 'Trinidad and Tobago', isPriority: false },
    { value: 'Tunisia', label: 'Tunisia', isPriority: false },
    { value: 'Turkey', label: 'Turkey', isPriority: false },
    { value: 'Turkmenistan', label: 'Turkmenistan', isPriority: false },
    { value: 'Tuvalu', label: 'Tuvalu', isPriority: false },
    { value: 'Uganda', label: 'Uganda', isPriority: false },
    { value: 'Ukraine', label: 'Ukraine', isPriority: false },
    { value: 'United Arab Emirates', label: 'United Arab Emirates', isPriority: false },
    { value: 'Uruguay', label: 'Uruguay', isPriority: false },
    { value: 'Uzbekistan', label: 'Uzbekistan', isPriority: false },
    { value: 'Vanuatu', label: 'Vanuatu', isPriority: false },
    { value: 'Vatican City', label: 'Vatican City', isPriority: false },
    { value: 'Venezuela', label: 'Venezuela', isPriority: false },
    { value: 'Vietnam', label: 'Vietnam', isPriority: false },
    { value: 'Yemen', label: 'Yemen', isPriority: false },
    { value: 'Zambia', label: 'Zambia', isPriority: false },
    { value: 'Zimbabwe', label: 'Zimbabwe', isPriority: false },
    { value: 'Other', label: 'Other', isPriority: false }
];

const SignUpModal: React.FC<SignUpModalProps> = ({ onClose, onSignupSuccess, onOAuthSignupPhaseChange, skipReloadOnSuccess, returnAfterAuth }) => {
    // Not a public page's modal: no return to an older page.
    useEffect(() => { if (!returnAfterAuth) clearReturnAfterAuth(); }, [returnAfterAuth]);
    const { signIn, isLoaded: isSignInLoaded } = useSignIn();
    const clerk = useClerk();
    const { user, isLoaded: isUserLoaded } = useUser();
    const { session, isLoaded: isSessionLoaded } = useSession();
    
    const [step, setStep] = useState<'email' | 'otp' | 'form'>('email');
    const [formState, setFormState] = useState({
        email: '',
        firstName: '',
        lastName: '',
        country: '',
        organisation: '',
        designation: '',
        linkedIn: ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
    const [errorMessage, setErrorMessage] = useState('');
    const [emailStatus, setEmailStatus] = useState<'idle' | 'invalid' | 'checking' | 'exists' | 'available' | 'moved'>('idle');
    const [movedMessage, setMovedMessage] = useState('');
    const [isCountryDropdownOpen, setIsCountryDropdownOpen] = useState(false);
    const [countrySearchTerm, setCountrySearchTerm] = useState('');
    const [otp, setOtp] = useState(['', '', '', '']);
    const [timer, setTimer] = useState(0);
    const [otpId, setOtpId] = useState<string | null>(null);
    const [isEmailVerified, setIsEmailVerified] = useState(false);
    const [isLinkedInLoading, setIsLinkedInLoading] = useState(false);
    const [isGoogleLoading, setIsGoogleLoading] = useState(false);
    const [linkedInDataLoaded, setLinkedInDataLoaded] = useState(false);
    const [showLoginConfirmation, setShowLoginConfirmation] = useState(false);
    const [linkedInUserData, setLinkedInUserData] = useState<{ email: string; firstName: string; lastName: string; linkedIn: string; company?: string; designation?: string; imageUrl?: string } | null>(null);
    const [isAutoCreatingAccount, setIsAutoCreatingAccount] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const inputRefs = [
        useRef<HTMLInputElement>(null), 
        useRef<HTMLInputElement>(null), 
        useRef<HTMLInputElement>(null), 
        useRef<HTMLInputElement>(null)
    ];

    // Email validation regex
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    
    // Form validation - LinkedIn URL is optional, all other fields are required
    const isFormValid = step === 'form' && 
        formState.firstName.trim().length > 0 &&
        formState.lastName.trim().length > 0 &&
        formState.email.trim().length > 0 &&
        formState.country.trim().length > 0 &&
        formState.organisation.trim().length > 0 &&
        formState.designation.trim().length > 0;
        // linkedIn is optional, so we don't check it

    // Filtered countries based on search term
    const filteredCountries = COUNTRIES.filter(country => 
        country.value === 'separator' || 
        country.label.toLowerCase().includes(countrySearchTerm.toLowerCase())
    );

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsCountryDropdownOpen(false);
                setCountrySearchTerm('');
            }
        };

        if (isCountryDropdownOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isCountryDropdownOpen]);

    // Timer countdown
    useEffect(() => {
        if (timer > 0) {
            const interval = setInterval(() => {
                setTimer(prev => prev - 1);
            }, 1000);
            return () => clearInterval(interval);
        }
    }, [timer]);

    // Track modal open
    useEffect(() => {
        trackGTMEvent('signup_modal_opened', {
            component: 'SignUpModal'
        });
    }, []);

    // Handle OAuth callback (LinkedIn or Google) - extract user info and auto-create account
    useEffect(() => {
        const handleOAuthCallback = async () => {
            if (isUserLoaded && isSessionLoaded && user && session && !linkedInDataLoaded) {
                onOAuthSignupPhaseChange?.(true);
                try {
                    setLinkedInDataLoaded(true);
                    
                    console.log('🔵 ========== OAUTH SIGNUP PROFILE DATA ==========');
                    console.log('📋 Full Clerk User Object:', user);
                    console.log('📧 Primary Email:', user.primaryEmailAddress?.emailAddress);
                    console.log('📧 All Email Addresses:', user.emailAddresses);
                    console.log('👤 First Name:', user.firstName);
                    console.log('👤 Last Name:', user.lastName);
                    console.log('🆔 User ID:', user.id);
                    console.log('📸 Image URL:', user.imageUrl);
                    console.log('🔗 External Accounts:', user.externalAccounts);
                    console.log('📝 Public Metadata:', user.publicMetadata);
                    console.log('📊 Unsafe Metadata:', user.unsafeMetadata);
                    console.log('📅 Created At:', user.createdAt);
                    console.log('📅 Updated At:', user.updatedAt);
                    console.log('✅ Verified:', user.emailAddresses?.find(e => e.id === user.primaryEmailAddressId)?.verification?.status);
                    console.log('🔐 Session:', session);
                    console.log('🔐 Session ID:', session?.id);
                    console.log('🔐 Session Status:', session?.status);
                    
                    const email = user.primaryEmailAddress?.emailAddress || '';
                    const firstName = user.firstName || '';
                    const lastName = user.lastName || '';
                    
                    let linkedinUrl = '';
                    let company = '';
                    let designation = '';
                    const oauthAccounts = user.externalAccounts || [];
                    console.log('🔍 Processing OAuth Accounts:', oauthAccounts.length);
                    
                    for (const account of oauthAccounts) {
                        const provider = account.provider as string;
                        console.log(`  - Provider: ${provider}`);
                        console.log(`  - Account ID: ${account.id}`);
                        console.log(`  - Username: ${account.username}`);
                        console.log(`  - Public Metadata:`, account.publicMetadata);
                        console.log(`  - Unsafe Metadata:`, (account as any).unsafeMetadata);
                        console.log(`  - Verification Status:`, account.verification?.status);
                        
                        if (provider === 'oauth_linkedin' || provider === 'oauth_linkedin_oidc' || provider.includes('linkedin')) {
                            const publicMetadata = account.publicMetadata as any;
                            const unsafeMetadata = (account as any).unsafeMetadata as any;
                            console.log(`  ✅ Found LinkedIn account!`);
                            console.log(`  📋 LinkedIn Public Metadata:`, publicMetadata);
                            console.log(`  📋 LinkedIn Unsafe Metadata:`, unsafeMetadata);
                            
                            if (!linkedinUrl && publicMetadata?.profile_url) {
                                linkedinUrl = publicMetadata.profile_url;
                            }
                            if (!linkedinUrl && unsafeMetadata?.profile_url) {
                                linkedinUrl = unsafeMetadata.profile_url;
                            }
                            if (!linkedinUrl && publicMetadata?.linkedin_url) {
                                linkedinUrl = publicMetadata.linkedin_url;
                            }
                            if (!linkedinUrl && unsafeMetadata?.linkedin_url) {
                                linkedinUrl = unsafeMetadata.linkedin_url;
                            }
                            if (!linkedinUrl && account.username) {
                                linkedinUrl = `https://www.linkedin.com/in/${account.username}`;
                            }
                            if (!linkedinUrl && (account as any).identifier) {
                                const identifier = (account as any).identifier;
                                if (identifier.includes('linkedin.com') || identifier.startsWith('in/')) {
                                    linkedinUrl = identifier.startsWith('http') ? identifier : `https://www.linkedin.com/${identifier.startsWith('in/') ? identifier : `in/${identifier}`}`;
                                }
                            }
                            
                            if (!company && (publicMetadata?.company || publicMetadata?.company_name || publicMetadata?.organization)) {
                                company = publicMetadata.company || publicMetadata.company_name || publicMetadata.organization;
                            }
                            if (!company && (unsafeMetadata?.company || unsafeMetadata?.company_name || unsafeMetadata?.organization)) {
                                company = unsafeMetadata.company || unsafeMetadata.company_name || unsafeMetadata.organization;
                            }
                            
                            if (!designation && (publicMetadata?.title || publicMetadata?.designation || publicMetadata?.position || publicMetadata?.job_title)) {
                                designation = publicMetadata.title || publicMetadata.designation || publicMetadata.position || publicMetadata.job_title;
                            }
                            if (!designation && (unsafeMetadata?.title || unsafeMetadata?.designation || unsafeMetadata?.position || unsafeMetadata?.job_title)) {
                                designation = unsafeMetadata.title || unsafeMetadata.designation || unsafeMetadata.position || unsafeMetadata.job_title;
                            }
                            
                            break;
                        }

                        if (provider === 'oauth_google' || provider.includes('google')) {
                            console.log(`  ✅ Found Google account!`);
                        }
                    }
                    
                    const userPublicMetadata = user.publicMetadata as any;
                    const userUnsafeMetadata = user.unsafeMetadata as any;
                    
                    if (!linkedinUrl && (userPublicMetadata?.linkedin_url || userPublicMetadata?.profile_url)) {
                        linkedinUrl = userPublicMetadata.linkedin_url || userPublicMetadata.profile_url;
                    }
                    if (!linkedinUrl && (userUnsafeMetadata?.linkedin_url || userUnsafeMetadata?.profile_url)) {
                        linkedinUrl = userUnsafeMetadata.linkedin_url || userUnsafeMetadata.profile_url;
                    }
                    
                    if (!company && (userPublicMetadata?.company || userPublicMetadata?.company_name || userUnsafeMetadata?.company || userUnsafeMetadata?.company_name)) {
                        company = userPublicMetadata?.company || userPublicMetadata?.company_name || userUnsafeMetadata?.company || userUnsafeMetadata?.company_name;
                    }
                    
                    if (!designation && (userPublicMetadata?.title || userPublicMetadata?.designation || userUnsafeMetadata?.title || userUnsafeMetadata?.designation)) {
                        designation = userPublicMetadata?.title || userPublicMetadata?.designation || userUnsafeMetadata?.title || userUnsafeMetadata?.designation;
                    }
                    
                    const imageUrl = user.imageUrl || '';
                    
                    const userData = {
                        email: email.toLowerCase(),
                        firstName,
                        lastName,
                        linkedIn: linkedinUrl,
                        company: company || undefined,
                        designation: designation || undefined,
                        imageUrl: imageUrl || undefined
                    };
                    console.log('📦 Extracted User Data:', userData);
                    console.log('🔵 ================================================');
                    
                    setLinkedInUserData(userData);
                    
                    if (email) {
                        setEmailStatus('checking');
                        try {
                            const response = await apiService.checkEmail(email.toLowerCase());
                            if (response.success && response.data?.exists) {
                                setEmailStatus('exists');
                                setShowLoginConfirmation(true);
                                trackGTMEvent('signup_oauth_email_exists', {
                                    component: 'SignUpModal',
                                    email: email.toLowerCase()
                                });
                            } else {
                                setEmailStatus('available');
                                await autoCreateAccountFromLinkedIn(userData);
                            }
                        } catch (error) {
                            console.error('Email check failed:', error);
                            await autoCreateAccountFromLinkedIn(userData);
                        }
                    }
                    
                    trackGTMEvent('signup_oauth_data_loaded', {
                        component: 'SignUpModal',
                        email: email.toLowerCase()
                    });
                } catch (error) {
                    console.error('Error processing OAuth callback:', error);
                    setErrorMessage('Failed to load profile information. Please try again.');
                } finally {
                    onOAuthSignupPhaseChange?.(false);
                }
            }
        };
        
        handleOAuthCallback();
    }, [isUserLoaded, isSessionLoaded, user, session, linkedInDataLoaded, onOAuthSignupPhaseChange]);

    // Helper function to convert image URL to base64 with proper data URL prefix
    const convertImageUrlToBase64 = async (imageUrl: string): Promise<string | null> => {
        try {
            console.log('🖼️ Converting image URL to base64:', imageUrl);
            const response = await fetch(imageUrl);
            if (!response.ok) {
                throw new Error(`Failed to fetch image: ${response.statusText}`);
            }
            
            // Determine image type from response or URL
            const contentType = response.headers.get('content-type') || 'image/jpeg';
            const blob = await response.blob();
            
            return new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onloadend = () => {
                    const base64String = reader.result as string;
                    // Ensure we have the full data URL with prefix
                    // If it already has the prefix, use it as-is, otherwise add it
                    let finalBase64 = base64String;
                    if (!base64String.startsWith('data:')) {
                        // Determine MIME type from content type or default to jpeg
                        const mimeType = contentType.startsWith('image/') 
                            ? contentType 
                            : 'image/jpeg';
                        finalBase64 = `data:${mimeType};base64,${base64String.includes(',') ? base64String.split(',')[1] : base64String}`;
                    }
                    console.log('✅ Image converted to base64 with prefix (length:', finalBase64.length, 'chars)');
                    console.log('   - MIME type:', contentType);
                    console.log('   - Prefix:', finalBase64.substring(0, 30) + '...');
                    resolve(finalBase64);
                };
                reader.onerror = reject;
                reader.readAsDataURL(blob);
            });
        } catch (error) {
            console.error('❌ Failed to convert image URL to base64:', error);
            return null;
        }
    };

    // Auto-create account from LinkedIn data
    const autoCreateAccountFromLinkedIn = async (userData: { email: string; firstName: string; lastName: string; linkedIn: string; company?: string; designation?: string; imageUrl?: string }) => {
        setIsAutoCreatingAccount(true);
        setSubmitStatus('idle');
        setErrorMessage('');
        
        trackGTMEvent('signup_linkedin_auto_create', {
            component: 'SignUpModal',
            email: userData.email
        });
        
        try {
            const signupData = {
                email: userData.email,
                first_name: userData.firstName || '',
                last_name: userData.lastName || '',
                country: '', // Will be filled later by user
                organisation: userData.company || '', // Use company from LinkedIn if available
                designation: userData.designation || '', // Use designation from LinkedIn if available
                linkedin_url: userData.linkedIn || ''
            };
            
            console.log('📤 Sending signup data with LinkedIn profile info:', signupData);
            console.log('   - Email:', signupData.email);
            console.log('   - First Name:', signupData.first_name);
            console.log('   - Last Name:', signupData.last_name);
            console.log('   - LinkedIn URL:', signupData.linkedin_url || '(not provided)');
            console.log('   - Company:', signupData.organisation || '(not provided)');
            console.log('   - Designation:', signupData.designation || '(not provided)');
            
            const response = await apiService.signup(signupData);
            
            if (response.success) {
                setSubmitStatus('success');
                
                trackGTMEvent('signup_linkedin_auto_create_success', {
                    component: 'SignUpModal',
                    email: userData.email
                });
                
                // Get user_id from response for professional summary generation
                const userId = response.data?.user_id;
                
                // Try to auto-login using Clerk session
                if (session) {
                    try {
                        const clerkToken = await session.getToken();
                        if (clerkToken) {
                            const loginResponse = await apiService.loginWithClerk(clerkToken);
                            if (loginResponse.success && loginResponse.data) {
                                const finalUserId = loginResponse.data.user_id || userId;
                                apiService.setStoredAccessToken(loginResponse.data.access_token);
                                apiService.setStoredRefreshToken(loginResponse.data.refresh_token);
                                apiService.setStoredUserId(finalUserId);
                                
                                // Clear signup mode
                                sessionStorage.removeItem('clerk_signup_mode');
                                
                                // A new account: when it started on a public tech page,
                                // onboarding in the app first, then back to that page.
                                // Only when this modal is a public page's (returnAfterAuth).
                                const back = returnAfterAuth ? updateReturnAfterAuth({ newAccount: true }) : null;
                                if (back && window.location.pathname !== '/') {
                                    armReturnAfterAuth();
                                    window.location.replace(withNext('/', back.path));
                                    return;
                                }
                                // Reload immediately after tokens are stored. Previously we awaited: fetch avatar →
                                // base64 → large profile update → enhanceText → another profile update — often 10–30s+
                                // on mobile. Users can add photo/summary from Settings later.
                                window.location.reload();
                                return;
                            }
                        }
                    } catch (loginError) {
                        console.error('Auto-login after LinkedIn signup failed:', loginError);
                    }
                }
                
                // If auto-login didn't work but we have userId, still try to update profile picture and generate summary
                if (userId) {
                    // Update profile picture from LinkedIn image URL
                    if (userData.imageUrl) {
                        console.log('🖼️ Updating profile picture from LinkedIn image (without auto-login)...');
                        try {
                            const base64Image = await convertImageUrlToBase64(userData.imageUrl);
                            if (base64Image) {
                                try {
                                    await apiService.updateProfile(userId, {
                                        profile_picture_url: base64Image
                                    });
                                    console.log('✅ Profile picture updated successfully');
                                } catch (imageUpdateError) {
                                    console.warn('⚠️ Failed to update profile picture:', imageUpdateError);
                                }
                            }
                        } catch (imageError) {
                            console.error('❌ Error updating profile picture:', imageError);
                        }
                    }
                    
                    // Generate professional summary
                    if (userData.linkedIn) {
                        console.log('🔄 Triggering professional summary generation (without auto-login)...');
                        try {
                            const summaryResponse = await apiService.enhanceText(
                                `${userData.firstName} ${userData.lastName}${userData.company ? ` at ${userData.company}` : ''}${userData.designation ? ` - ${userData.designation}` : ''}`,
                                'professional_summary',
                                userId,
                                userData.linkedIn
                            );
                            
                            if (summaryResponse.success && summaryResponse.data?.enhanced_text) {
                                console.log('✅ Professional summary generated successfully');
                                // Update profile with generated summary
                                try {
                                    await apiService.updateProfile(userId, {
                                        professional_summary: summaryResponse.data.enhanced_text
                                    });
                                } catch (updateError) {
                                    console.warn('⚠️ Failed to update profile with summary:', updateError);
                                }
                            }
                        } catch (summaryError) {
                            console.error('❌ Error generating professional summary:', summaryError);
                        }
                    }
                }
                
                if (onSignupSuccess) {
                    onSignupSuccess(userData.email);
                }
                
                setTimeout(() => {
                    handleModalClose('success');
                }, 2000);
            } else if (response.status === 409) {
                // The address already has an account (in other letter case
                // the e-mail check above missed): offer to log in instead.
                setEmailStatus('exists');
                setShowLoginConfirmation(true);
            } else {
                setSubmitStatus('error');
                setErrorMessage(friendlyAuthError(response.error, 'Failed to create account. Please try again.'));
            }
        } catch (error) {
            setSubmitStatus('error');
            setErrorMessage('Network error. Please try again.');
        } finally {
            setIsAutoCreatingAccount(false);
        }
    };

    // Handle login confirmation (when user with LinkedIn email already exists)
    const handleLoginConfirmYes = async () => {
        setShowLoginConfirmation(false);
        
        trackGTMEvent('signup_linkedin_login_confirmed', {
            component: 'SignUpModal',
            email: linkedInUserData?.email
        });
        
        // User wants to login - use Clerk session to authenticate
        if (session) {
            try {
                const clerkToken = await session.getToken();
                if (clerkToken) {
                    const loginResponse = await apiService.loginWithClerk(clerkToken);
                    if (loginResponse.success && loginResponse.data) {
                        apiService.setStoredAccessToken(loginResponse.data.access_token);
                        apiService.setStoredRefreshToken(loginResponse.data.refresh_token);
                        apiService.setStoredUserId(loginResponse.data.user_id);
                        
                        // Clear signup mode
                        sessionStorage.removeItem('clerk_signup_mode');
                        
                        // Reload to complete login
                        window.location.reload();
                        return;
                    }
                    // Suspended by the team: say so, and end the Clerk session,
                    // which would otherwise be retried (and refused) on every load.
                    if (loginResponse.error === 'account_disabled') {
                        setSubmitStatus('error');
                        setErrorMessage(friendlyAuthError(loginResponse.error));
                        sessionStorage.removeItem('clerk_signup_mode');
                        // signOut reloads the page; the app-wide suspended notice
                        // (index.tsx) picks this up afterwards.
                        try { sessionStorage.setItem('utech_account_suspended', '1'); } catch { /* ignore */ }
                        try { await clerk.signOut(); } catch { /* already gone */ }
                        return;
                    }
                }
            } catch (error) {
                console.error('Login with Clerk failed:', error);
                setErrorMessage('Login failed. Please try again.');
            }
        }
    };

    // Handle login confirmation declined
    const handleLoginConfirmNo = async () => {
        setShowLoginConfirmation(false);
        
        trackGTMEvent('signup_linkedin_login_declined', {
            component: 'SignUpModal',
            email: linkedInUserData?.email
        });
        
        // Clear Clerk session
        try {
            await clerk.signOut();
        } catch (error) {
            console.warn('Error signing out from Clerk:', error);
        }
        
        // Clear signup mode and close modal
        sessionStorage.removeItem('clerk_signup_mode');
        onClose();
    };

    // Email checking effect
    useEffect(() => {
        const checkEmail = async () => {
            const lowerEmail = formState.email.trim().toLowerCase();
            
            if (lowerEmail === '') {
                setEmailStatus('idle');
                return;
            }
            
            if (!emailRegex.test(lowerEmail)) {
                setEmailStatus('invalid');
                trackGTMEvent('signup_email_invalid', {
                    component: 'SignUpModal',
                    email: lowerEmail
                });
                return;
            }

            // Check email with API
            setEmailStatus('checking');
            try {
                const response = await apiService.checkEmail(lowerEmail);
                if (response.success && response.data) {
                    if (response.data.exists) {
                        setEmailStatus('exists');
                        trackGTMEvent('signup_email_exists', {
                            component: 'SignUpModal',
                            email: lowerEmail
                        });
                    } else if (response.data.moved) {
                        // Retired by an admin e-mail change: no new account here.
                        setMovedMessage(response.data.message || '');
                        setEmailStatus('moved');
                    } else {
                        setEmailStatus('available');
                        trackGTMEvent('signup_email_available', {
                            component: 'SignUpModal',
                            email: lowerEmail
                        });
                    }
                } else {
                    setEmailStatus('available'); // Default to available on API error
                }
            } catch (error) {
                console.error('Email check failed:', error);
                setEmailStatus('available'); // Default to available on network error
            }
        };

        // Debounce the email check
        const timeoutId = setTimeout(checkEmail, 500);
        return () => clearTimeout(timeoutId);
    }, [formState.email]);

    // OTP input handling
    const handleOtpChange = (index: number, value: string) => {
        if (value.length > 1) return; // Only allow single digit
        if (!/^\d*$/.test(value)) return; // Only allow digits
        
        const newOtp = [...otp];
        newOtp[index] = value;
        setOtp(newOtp);

        // Auto-focus next input
        if (value && index < 3) {
            inputRefs[index + 1].current?.focus();
        }
    };

    const handleOtpKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Backspace' && !otp[index] && index > 0) {
            inputRefs[index - 1].current?.focus();
        }
    };

    const handleOtpPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
        e.preventDefault();
        const pastedData = e.clipboardData.getData('text').slice(0, 4);
        if (/^\d+$/.test(pastedData)) {
            const newOtp = pastedData.split('').concat(['', '', '', '']).slice(0, 4);
            setOtp(newOtp);
            // Focus last filled input
            const lastIndex = Math.min(pastedData.length - 1, 3);
            inputRefs[lastIndex].current?.focus();
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormState(prevState => ({ ...prevState, [name]: value }));
    };

    const handleCountrySelect = (country: string) => {
        if (country !== 'separator') {
            setFormState(prevState => ({ ...prevState, country }));
            setIsCountryDropdownOpen(false);
            setCountrySearchTerm('');
            trackGTMEvent('signup_country_selected', {
                component: 'SignUpModal',
                country: country
            });
        }
    };

    const handleSendOTP = async () => {
        if (emailStatus !== 'available') return;
        
        setIsSubmitting(true);
        setErrorMessage('');
        
        const normalizedEmail = formState.email.trim().toLowerCase();
        trackGTMEvent('signup_otp_send_attempt', {
            component: 'SignUpModal',
            email: normalizedEmail
        });
        
        try {
            const response = await apiService.sendOTP(normalizedEmail, 'registration');
            if (response.success && response.data) {
                setOtpId(response.data.otp_id);
                setStep('otp');
                setTimer(response.data.ttl_sec || 25);
                trackGTMEvent('signup_otp_sent', {
                    component: 'SignUpModal',
                    email: normalizedEmail
                });
            } else {
                setErrorMessage(friendlyAuthError(response.error, 'Failed to send OTP. Please try again.'));
                trackGTMEvent('signup_otp_send_error', {
                    component: 'SignUpModal',
                    email: normalizedEmail,
                    error: response.error || 'Unknown error'
                });
            }
        } catch (error) {
            setErrorMessage('Network error. Please check your connection and try again.');
            trackGTMEvent('signup_otp_send_network_error', {
                component: 'SignUpModal',
                email: normalizedEmail
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleResendOTP = async () => {
        if (!otpId) return;
        
        setIsSubmitting(true);
        setErrorMessage('');
        
        try {
            const response = await apiService.resendOTP(otpId);
            if (response.success && response.data) {
                setTimer(response.data.ttl_sec || 25);
                setOtp(['', '', '', '']);
                inputRefs[0].current?.focus();
                trackGTMEvent('signup_otp_resent', {
                    component: 'SignUpModal',
                    email: formState.email.trim().toLowerCase()
                });
            } else {
                setErrorMessage(friendlyAuthError(response.error, 'Failed to resend OTP. Please try again.'));
            }
        } catch (error) {
            setErrorMessage('Network error. Please check your connection and try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleVerifyOTP = async () => {
        const otpCode = otp.join('');
        if (otpCode.length !== 4) {
            setErrorMessage('Please enter the complete 4-digit OTP code.');
            return;
        }
        
        setIsSubmitting(true);
        setErrorMessage('');
        
        const normalizedEmail = formState.email.trim().toLowerCase();
        trackGTMEvent('signup_otp_verify_attempt', {
            component: 'SignUpModal',
            email: normalizedEmail
        });
        
        try {
            const response = await apiService.verifyOTP(normalizedEmail, otpCode, 'registration');
            if (response.success) {
                setIsEmailVerified(true);
                setStep('form');
                trackGTMEvent('signup_otp_verified', {
                    component: 'SignUpModal',
                    email: normalizedEmail
                });
            } else {
                setErrorMessage(friendlyAuthError(response.error, 'Invalid OTP code. Please try again.'));
                setOtp(['', '', '', '']);
                inputRefs[0].current?.focus();
                trackGTMEvent('signup_otp_verify_error', {
                    component: 'SignUpModal',
                    email: normalizedEmail,
                    error: response.error || 'Unknown error'
                });
            }
        } catch (error) {
            setErrorMessage('Network error. Please check your connection and try again.');
            trackGTMEvent('signup_otp_verify_network_error', {
                component: 'SignUpModal',
                email: normalizedEmail
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!isFormValid || isSubmitting || !isEmailVerified) return;

        trackClick('pre_login_submit_signup');
        const normalizedEmail = formState.email.trim().toLowerCase();
        
        // Double-check email doesn't exist before submitting
        setIsSubmitting(true);
        setSubmitStatus('idle');
        setErrorMessage('');
        
        try {
            // Final email check before signup
            const emailCheckResponse = await apiService.checkEmail(normalizedEmail);
            if (emailCheckResponse.success && emailCheckResponse.data?.moved) {
                setSubmitStatus('error');
                setErrorMessage(emailCheckResponse.data.message || 'This e-mail address is no longer used to sign in — the account moved to a different address. Sign in with your current e-mail address, or write to info@universitytech.io.');
                setEmailStatus('moved');
                setIsSubmitting(false);
                return;
            }
            if (emailCheckResponse.success && emailCheckResponse.data?.exists) {
                setSubmitStatus('error');
                setErrorMessage('An account with this email already exists. Please login instead.');
                setEmailStatus('exists');
                setIsSubmitting(false);
                trackGTMEvent('signup_prevented_email_exists', {
                    component: 'SignUpModal',
                    email: normalizedEmail
                });
                return;
            }
        } catch (error) {
            console.error('Final email check failed:', error);
            // Continue with signup on error (user might have just registered)
        }

        trackGTMEvent('signup_form_submitted', {
            component: 'SignUpModal',
            email: normalizedEmail,
            country: formState.country,
            has_linkedin: !!formState.linkedIn
        });

        try {
            const signupPayload = {
                first_name: formState.firstName,
                last_name: formState.lastName,
                email: normalizedEmail,
                country: formState.country,
                organisation: formState.organisation,
                designation: formState.designation,
                linkedin_url: formState.linkedIn || undefined,
            };
            
            console.log('📤 Sending manual signup data:', signupPayload);
            console.log('   - Email:', signupPayload.email);
            console.log('   - First Name:', signupPayload.first_name);
            console.log('   - Last Name:', signupPayload.last_name);
            console.log('   - Country:', signupPayload.country);
            console.log('   - Organisation:', signupPayload.organisation);
            console.log('   - Designation:', signupPayload.designation);
            console.log('   - LinkedIn URL:', signupPayload.linkedin_url || '(not provided)');
            
            const response = await apiService.signup(signupPayload);

            if (response.success) {
                setSubmitStatus('success');
                trackGTMEvent('signup_submission_success', {
                    component: 'SignUpModal',
                    email: normalizedEmail,
                    country: formState.country
                });
                
                // Presence only — tokens and the response body never go to the
                // console (PostHog session recording captures it).
                console.log('🔍 Signup response:', response.success ? 'ok' : 'failed',
                  { access_token: !!response.data?.access_token, refresh_token: !!response.data?.refresh_token });
                
                // Automatically log in the user after successful signup
                try {
                    let loginSuccess = false;
                    
                    // Check for tokens in response.data
                    // Backend returns: { success: true, user_id: "...", access_token: "...", refresh_token: "..." }
                    // Frontend wraps it as: { success: true, data: { success: true, user_id: "...", ... } }
                    const accessToken = response.data?.access_token;
                    const refreshToken = response.data?.refresh_token;
                    const userId = response.data?.user_id;
                    
                    // First, check if signup response includes login tokens (backend returns them)
                    if (accessToken && refreshToken && userId) {
                        console.log('✅ Found tokens in signup response, storing them...');
                        // Store session tokens from signup response
                        apiService.setStoredAccessToken(accessToken);
                        apiService.setStoredRefreshToken(refreshToken);
                        apiService.setStoredUserId(userId);
                        
                        // Clear signup mode flag
                        sessionStorage.removeItem('clerk_signup_mode');
                        
                        loginSuccess = true;
                        console.log('✅ Auto-login successful with signup tokens');
                    } else {
                        console.warn('⚠️ No tokens found in signup response. Response structure:', {
                            hasData: !!response.data,
                            dataKeys: response.data ? Object.keys(response.data) : [],
                            accessToken: accessToken,
                            refreshToken: refreshToken,
                            userId: userId,
                            fullData: response.data
                        });
                    }
                    
                    // Try Clerk login (if available from LinkedIn OAuth)
                    if (!loginSuccess && session && isSessionLoaded) {
                        try {
                            const clerkToken = await session.getToken();
                            if (clerkToken) {
                                const loginResponse = await apiService.loginWithClerk(clerkToken);
                                if (loginResponse.success && loginResponse.data) {
                                    // Store session tokens
                                    apiService.setStoredAccessToken(loginResponse.data.access_token);
                                    apiService.setStoredRefreshToken(loginResponse.data.refresh_token);
                                    apiService.setStoredUserId(loginResponse.data.user_id);
                                    
                                    // Clear signup mode flag
                                    sessionStorage.removeItem('clerk_signup_mode');
                                    
                                    loginSuccess = true;
                                }
                            }
                        } catch (clerkError) {
                            console.warn('Clerk auto-login failed, trying OTP login:', clerkError);
                        }
                    }
                    
                    // If still not logged in, try OTP login using the same OTP code
                    if (!loginSuccess && otpId && otp.join('').length === 4) {
                        try {
                            const otpCode = otp.join('');
                            // Try using the same OTP for login (backend might allow this for recently verified emails)
                            const loginResponse = await apiService.verifyOTP(normalizedEmail, otpCode, 'login');
                            if (loginResponse.success && loginResponse.data) {
                                // Store session tokens
                                apiService.setStoredAccessToken(loginResponse.data.access_token);
                                apiService.setStoredRefreshToken(loginResponse.data.refresh_token);
                                apiService.setStoredUserId(loginResponse.data.user_id);
                                
                                loginSuccess = true;
                            }
                        } catch (otpError) {
                            console.warn('OTP auto-login failed (same OTP code may not work for login purpose):', otpError);
                            // If same OTP doesn't work, user will need to login manually
                        }
                    }
                    
                    // If login succeeded, call success callback and close modal
                    if (loginSuccess) {
                        // Clear signup mode flag
                        sessionStorage.removeItem('clerk_signup_mode');
                        
                        console.log('✅ Auto-login completed, triggering app state update...');
                        
                        // Call success callback with login
                        if (onSignupSuccess) {
                            onSignupSuccess(normalizedEmail);
                        }
                        
                        // Reload the page to ensure app state is updated with new login
                        // This ensures the app recognizes the user is logged in
                        // (unless the parent navigates itself — a public tech page).
                        if (skipReloadOnSuccess) return;
                        setTimeout(() => {
                            console.log('🔄 Reloading page to update app state...');
                            window.location.reload();
                        }, 500);
                        return; // Exit early since we logged in
                    }
                } catch (loginError) {
                    console.error('Auto-login after signup failed:', loginError);
                    // Continue with normal flow (show success message, user can login manually)
                }
                
                // If auto-login didn't work, show success message and close modal
                // Call success callback if provided
                if (onSignupSuccess) {
                    onSignupSuccess(normalizedEmail);
                }
                
                // Close modal after showing success
                setTimeout(() => {
                    handleModalClose('success');
                }, 2000);
            } else {
                setSubmitStatus('error');
                setErrorMessage(friendlyAuthError(response.error, 'Failed to create account. Please try again.'));
                trackGTMEvent('signup_submission_error', {
                    component: 'SignUpModal',
                    email: normalizedEmail,
                    error: response.error || 'Unknown error'
                });
            }
        } catch (error) {
            setSubmitStatus('error');
            setErrorMessage('Network error. Please check your connection and try again.');
            trackGTMEvent('signup_submission_network_error', {
                component: 'SignUpModal',
                email: formState.email.trim().toLowerCase()
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleLinkedInSignup = async () => {
        trackGTMEvent('signup_linkedin_clicked', {
            component: 'SignUpModal'
        });
        
        // Check if Clerk is loaded
        if (!isSignInLoaded || !isSessionLoaded) {
            setErrorMessage('Authentication service is loading. Please try again.');
            return;
        }
        
        if (!signIn) {
            setErrorMessage('Authentication service not available. Please refresh the page.');
            return;
        }
        
        setIsLinkedInLoading(true);
        setErrorMessage('');
        
        // Set flag in sessionStorage to indicate we're in signup mode
        sessionStorage.setItem('clerk_signup_mode', 'true');
        
        try {
            // Check if there's an existing session
            if (session) {
                console.log('Existing Clerk session detected, signing out first...');
                try {
                    await clerk.signOut();
                    await new Promise(resolve => setTimeout(resolve, 100));
                } catch (signOutError) {
                    console.warn('Sign out warning:', signOutError);
                }
            }
            
            // Start LinkedIn OAuth flow
            await signIn.authenticateWithRedirect({
                strategy: 'oauth_linkedin_oidc',
                // Back to a public page after auth when one is pending (utils/returnAfterAuth).
                ...ssoRedirectUrls('linkedin', !!returnAfterAuth),
            });
        } catch (error: any) {
            console.error('Clerk OAuth error:', error);
            sessionStorage.removeItem('clerk_signup_mode');
            
            if (error.errors) {
                const clerkError = error.errors[0];
                
                if (clerkError?.code === 'session_exists' || 
                    clerkError?.message?.includes('already signed in')) {
                    try {
                        await clerk.signOut();
                        document.cookie.split(";").forEach((c) => {
                            if (c.includes('__clerk') || c.includes('__session')) {
                                document.cookie = c
                                    .replace(/^ +/, "")
                                    .replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
                            }
                        });
                        await new Promise(resolve => setTimeout(resolve, 500));
                        await signIn.authenticateWithRedirect({
                            strategy: 'oauth_linkedin_oidc',
                            // Back to a public page after auth when one is pending (utils/returnAfterAuth).
                            ...ssoRedirectUrls('linkedin', !!returnAfterAuth),
                        });
                        return;
                    } catch (retryError) {
                        setErrorMessage('Session conflict. Please refresh the page and try again.');
                    }
                } else if (clerkError?.code === 'form_identifier_not_found') {
                    setErrorMessage('LinkedIn login is not properly configured. Please contact support.');
                } else {
                    setErrorMessage(clerkError?.message || 'LinkedIn signup failed. Please try again.');
                }
            } else if (error.message?.includes('already signed in')) {
                try {
                    await clerk.signOut();
                    await new Promise(resolve => setTimeout(resolve, 500));
                    await signIn.authenticateWithRedirect({
                        strategy: 'oauth_linkedin_oidc',
                        // Back to a public page after auth when one is pending (utils/returnAfterAuth).
                        ...ssoRedirectUrls('linkedin', !!returnAfterAuth),
                    });
                    return;
                } catch (retryError) {
                    setErrorMessage('Session conflict. Please refresh the page and try again.');
                }
            } else {
                setErrorMessage('LinkedIn signup failed. Please try again or use email signup.');
            }
            
            trackGTMEvent('signup_linkedin_error', {
                component: 'SignUpModal',
                error: error.message || 'Unknown error'
            });
        } finally {
            setIsLinkedInLoading(false);
        }
    };

    const handleGoogleSignup = async () => {
        trackGTMEvent('signup_google_clicked', { component: 'SignUpModal' });

        if (!isSignInLoaded || !isSessionLoaded) {
            setErrorMessage('Authentication service is loading. Please try again.');
            return;
        }
        if (!signIn) {
            setErrorMessage('Authentication service not available. Please refresh the page.');
            return;
        }

        setIsGoogleLoading(true);
        setErrorMessage('');
        sessionStorage.setItem('clerk_signup_mode', 'true');

        try {
            if (session) {
                try {
                    await clerk.signOut();
                    await new Promise(resolve => setTimeout(resolve, 100));
                } catch (signOutError) {
                    console.warn('Sign out warning:', signOutError);
                }
            }

            await signIn.authenticateWithRedirect({
                strategy: 'oauth_google',
                // Back to a public page after auth when one is pending (utils/returnAfterAuth).
                ...ssoRedirectUrls('google', !!returnAfterAuth),
            });
        } catch (error: any) {
            console.error('Clerk Google OAuth error:', error);
            sessionStorage.removeItem('clerk_signup_mode');

            if (error.errors) {
                const clerkError = error.errors[0];

                if (clerkError?.code === 'session_exists' ||
                    clerkError?.message?.includes('already signed in')) {
                    try {
                        await clerk.signOut();
                        document.cookie.split(";").forEach((c) => {
                            if (c.includes('__clerk') || c.includes('__session')) {
                                document.cookie = c
                                    .replace(/^ +/, "")
                                    .replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
                            }
                        });
                        await new Promise(resolve => setTimeout(resolve, 500));
                        await signIn.authenticateWithRedirect({
                            strategy: 'oauth_google',
                            // Back to a public page after auth when one is pending (utils/returnAfterAuth).
                            ...ssoRedirectUrls('google', !!returnAfterAuth),
                        });
                        return;
                    } catch (retryError) {
                        setErrorMessage('Session conflict. Please refresh the page and try again.');
                    }
                } else if (clerkError?.code === 'form_identifier_not_found') {
                    setErrorMessage('Google login is not properly configured. Please contact support.');
                } else {
                    setErrorMessage(clerkError?.message || 'Google signup failed. Please try again.');
                }
            } else if (error.message?.includes('already signed in')) {
                try {
                    await clerk.signOut();
                    await new Promise(resolve => setTimeout(resolve, 500));
                    await signIn.authenticateWithRedirect({
                        strategy: 'oauth_google',
                        // Back to a public page after auth when one is pending (utils/returnAfterAuth).
                        ...ssoRedirectUrls('google', !!returnAfterAuth),
                    });
                    return;
                } catch (retryError) {
                    setErrorMessage('Session conflict. Please refresh the page and try again.');
                }
            } else {
                setErrorMessage('Google signup failed. Please try again or use email signup.');
            }

            trackGTMEvent('signup_google_error', {
                component: 'SignUpModal',
                error: error.message || 'Unknown error'
            });
        } finally {
            setIsGoogleLoading(false);
        }
    };

    const handleModalClose = (reason?: 'back' | 'outside' | 'success') => {
        // Clear signup mode flag when closing (except on success, which is handled in handleSubmit)
        if (reason === 'success') {
            // Clear flag after a delay to allow success message to show
            setTimeout(() => {
                sessionStorage.removeItem('clerk_signup_mode');
            }, 2500);
        } else {
            sessionStorage.removeItem('clerk_signup_mode');
        }
        
        if (reason !== 'back') {
            trackGTMEvent('signup_modal_closed', {
                component: 'SignUpModal',
                form_filled: isFormValid,
                email_status: emailStatus,
                close_reason: reason || 'unknown'
            });
        }
        onClose();
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-[60] font-space-grotesk p-2 sm:p-4 overflow-y-auto">
            {/* Login Confirmation Popup */}
            {showLoginConfirmation && (
                <div className="fixed inset-0 bg-black bg-opacity-60 flex justify-center items-center z-[70] p-2 sm:p-4" onClick={(e) => e.stopPropagation()}>
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-4 sm:p-6 mx-2 sm:mx-4 my-auto">
                        <div className="text-center">
                            <div className="w-16 h-16 mx-auto mb-4 bg-blue-100 rounded-full flex items-center justify-center">
                                <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                </svg>
                            </div>
                            <h3 className="text-xl font-semibold text-gray-900 mb-2">Account Already Exists</h3>
                            <p className="text-gray-600 mb-6">
                                An account with <strong>{linkedInUserData?.email}</strong> already exists. Would you like to log in instead?
                            </p>
                            <div className="flex gap-3">
                                <button
                                    onClick={handleLoginConfirmNo}
                                    className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-full hover:bg-gray-200 transition-colors"
                                >
                                    No, Cancel
                                </button>
                                <button
                                    onClick={handleLoginConfirmYes}
                                    className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-primary-600 rounded-full hover:bg-primary-700 transition-colors"
                                >
                                    Yes, Log In
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Auto-creating account state */}
            {isAutoCreatingAccount && (
                <div className="fixed inset-0 bg-black bg-opacity-60 flex justify-center items-center z-[70] p-2 sm:p-4" onClick={(e) => e.stopPropagation()}>
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-4 sm:p-6 mx-2 sm:mx-4 my-auto">
                        <div className="text-center">
                            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                                <div className="animate-spin h-12 w-12 border-4 border-primary-600 border-t-transparent rounded-full"></div>
                            </div>
                            <h3 className="text-xl font-semibold text-gray-900 mb-2">Creating Your Account</h3>
                            <p className="text-gray-600">
                                Setting up your account...
                            </p>
                        </div>
                    </div>
                </div>
            )}

            <div className="bg-white rounded-2xl shadow-lg w-full max-w-3xl p-4 sm:p-6 md:p-8 relative max-h-[90vh] overflow-y-auto my-auto" onClick={e => e.stopPropagation()}>
                <div className="text-center mb-4 sm:mb-6">
                    <h2 className="text-xl sm:text-2xl font-medium text-grayscale-1300">
                        {step === 'email' && 'Sign Up'}
                        {step === 'otp' && 'Verify Your Email'}
                        {step === 'form' && 'Complete Your Profile'}
                    </h2>
                    <p className="text-xs sm:text-sm text-grayscale-1100 font-grotesk mt-1">
                        {step === 'email' && 'Enter your email to get started'}
                        {step === 'otp' && `We sent a verification code to ${formState.email}`}
                        {step === 'form' && 'Fill in your details to create your account'}
                    </p>
                </div>
                
                {/* Email Step */}
                {step === 'email' && (
                    <div className="space-y-4">
                        {/* Google OAuth Button */}
                        {ENABLE_GOOGLE_AUTH && (
                            <div>
                                <button
                                    type="button"
                                    onClick={handleGoogleSignup}
                                    disabled={isGoogleLoading || !isSignInLoaded}
                                    className="w-full flex items-center justify-center gap-3 px-4 py-3 text-sm font-medium text-grayscale-1400 bg-white border border-gray-300 rounded-full hover:bg-gray-50 transition-colors shadow-md disabled:bg-gray-100 disabled:cursor-not-allowed"
                                >
                                    {isGoogleLoading ? (
                                        <>
                                            <div className="animate-spin h-4 w-4 border-2 border-gray-600 border-t-transparent rounded-full"></div>
                                            <span>Connecting...</span>
                                        </>
                                    ) : (
                                        <>
                                            <svg className="w-5 h-5" viewBox="0 0 24 24">
                                                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                                                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                                                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                                                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                                            </svg>
                                            <span>Sign Up with Google</span>
                                        </>
                                    )}
                                </button>
                            </div>
                        )}

                        {/* LinkedIn OAuth Button */}
                        {ENABLE_LINKEDIN_AUTH && (
                            <div>
                                <button
                                    type="button"
                                    onClick={handleLinkedInSignup}
                                    disabled={isLinkedInLoading || !isSignInLoaded}
                                    className="w-full flex items-center justify-center gap-3 px-4 py-3 text-sm font-medium text-white bg-[#0077B5] rounded-full hover:bg-[#006399] transition-colors shadow-md disabled:bg-gray-400 disabled:cursor-not-allowed"
                                >
                                    {isLinkedInLoading ? (
                                        <>
                                            <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                                            <span>Connecting...</span>
                                        </>
                                    ) : (
                                        <>
                                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                                                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                                            </svg>
                                            <span>Sign Up with LinkedIn</span>
                                        </>
                                    )}
                                </button>
                            </div>
                        )}
                        
                        {/* OR divider - Shown when social auth buttons are enabled */}
                        {(ENABLE_LINKEDIN_AUTH || ENABLE_GOOGLE_AUTH) && (
                            <div className="flex items-center gap-2 my-4">
                                <div className="flex-1 h-px bg-gray-300"></div>
                                <span className="text-xs text-gray-500">OR</span>
                                <div className="flex-1 h-px bg-gray-300"></div>
                            </div>
                        )}
                        
                        <div>
                            <label htmlFor="email" className="block text-xs text-main-text mb-1">Email</label>
                            <div className="relative">
                                <input 
                                    id="email" 
                                    type="email" 
                                    name="email" 
                                    value={formState.email}
                                    onChange={handleInputChange}
                                    placeholder="Add your email address" 
                                    className={`w-full p-2.5 pr-10 text-sm bg-[#F5F5F5] rounded-md placeholder-gray-400 focus:outline-none focus:ring-1 ${
                                        emailStatus === 'invalid' ? 'focus:ring-red-500 border-red-500' :
                                        emailStatus === 'exists' ? 'focus:ring-blue-500 border-blue-500' :
                                        emailStatus === 'available' ? 'focus:ring-green-500 border-green-500' :
                                        'focus:ring-primary-600'
                                    }`}
                                />
                                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                                    {emailStatus === 'checking' && (
                                        <div className="animate-spin h-4 w-4 border-2 border-primary-600 border-t-transparent rounded-full"></div>
                                    )}
                                    {emailStatus === 'invalid' && (
                                        <span className="text-red-500 text-sm">❌</span>
                                    )}
                                    {emailStatus === 'exists' && (
                                        <span className="text-blue-500 text-sm">👤</span>
                                    )}
                                    {emailStatus === 'available' && (
                                        <span className="text-green-500 text-sm">✅</span>
                                    )}
                                </div>
                            </div>
                            {emailStatus === 'invalid' && (
                                <p className="text-red-500 text-xs mt-1">Please enter a valid email address</p>
                            )}
                            {emailStatus === 'exists' && (
                                <p className="text-blue-500 text-xs mt-1">Account already exists. Please login instead.</p>
                            )}
                            {emailStatus === 'moved' && (
                                <p className="text-red-500 text-xs mt-1">{movedMessage || 'This e-mail address is no longer used to sign in — the account moved to a different address. Sign in with your current e-mail address, or write to info@universitytech.io.'}</p>
                            )}
                            {emailStatus === 'available' && (
                                <p className="text-green-500 text-xs mt-1">Email available for signup</p>
                            )}
                        </div>
                    </div>
                )}

                {/* OTP Step */}
                {step === 'otp' && (
                    <div className="space-y-4">
                        <div>
                            <label className="block text-xs text-main-text mb-2">Enter 4-digit OTP</label>
                            <div className="flex gap-2 justify-center">
                                {otp.map((digit, index) => (
                                    <input
                                        key={index}
                                        ref={inputRefs[index]}
                                        type="text"
                                        inputMode="numeric"
                                        maxLength={1}
                                        value={digit}
                                        onChange={(e) => handleOtpChange(index, e.target.value)}
                                        onKeyDown={(e) => handleOtpKeyDown(index, e)}
                                        onPaste={index === 0 ? handleOtpPaste : undefined}
                                        className="w-14 h-14 text-center text-lg font-semibold border-2 border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-600 focus:border-primary-600"
                                    />
                                ))}
                            </div>
                            {errorMessage && (
                                <InlineAlert variant="error" compact className="mt-3">{errorMessage}</InlineAlert>
                            )}
                            <div className="mt-4 text-center">
                                {timer > 0 ? (
                                    <p className="text-sm text-gray-600">
                                        Resend OTP in {timer}s
                                    </p>
                                ) : (
                                    <button
                                        type="button"
                                        onClick={handleResendOTP}
                                        disabled={isSubmitting}
                                        className="text-sm text-primary-600 hover:text-primary-700 font-medium"
                                    >
                                        Resend OTP
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* Form Step */}
                {step === 'form' && (
                    <form className="space-y-4" onSubmit={handleSubmit}>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label htmlFor="firstName" className="block text-xs text-main-text mb-1">First Name</label>
                                <input id="firstName" type="text" name="firstName" value={formState.firstName} onChange={handleInputChange} placeholder="Enter first name" className="w-full p-2.5 text-sm bg-[#F5F5F5] rounded-md placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-primary-600"/>
                            </div>
                            <div>
                                <label htmlFor="lastName" className="block text-xs text-main-text mb-1">Last Name</label>
                                <input id="lastName" type="text" name="lastName" value={formState.lastName} onChange={handleInputChange} placeholder="Enter last name" className="w-full p-2.5 text-sm bg-[#F5F5F5] rounded-md placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-primary-600"/>
                            </div>
                        </div>

                        <div>
                            <label htmlFor="email-display" className="block text-xs text-main-text mb-1">Email</label>
                            <input id="email-display" type="email" value={formState.email} disabled className="w-full p-2.5 text-sm bg-gray-200 rounded-md text-gray-600 cursor-not-allowed"/>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="relative" ref={dropdownRef}>
                                <label htmlFor="country" className="block text-xs text-main-text mb-1">Country</label>
                                <div className="relative">
                                    <button
                                        type="button"
                                        onClick={() => setIsCountryDropdownOpen(!isCountryDropdownOpen)}
                                        className={`w-full p-2.5 text-sm bg-[#F5F5F5] rounded-md text-left flex items-center justify-between focus:outline-none focus:ring-1 focus:ring-primary-600 ${
                                            formState.country ? 'text-black' : 'text-gray-400'
                                        }`}
                                    >
                                        <span>{formState.country || 'Select a country'}</span>
                                        <span className={`transition-transform duration-200 ${isCountryDropdownOpen ? 'rotate-180' : ''}`}>
                                            <DownArrowIcon />
                                        </span>
                                    </button>
                                    
                                    {isCountryDropdownOpen && (
                                        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg">
                                            <div className="p-2 border-b border-gray-200">
                                                <input
                                                    type="text"
                                                    placeholder="Search countries..."
                                                    value={countrySearchTerm}
                                                    onChange={(e) => setCountrySearchTerm(e.target.value)}
                                                    className="w-full p-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-600"
                                                    onClick={(e) => e.stopPropagation()}
                                                />
                                            </div>
                                            
                                            <div className="max-h-[200px] overflow-y-auto">
                                                {filteredCountries.length > 0 ? (
                                                    filteredCountries.map((country, index) => (
                                                        country.value === 'separator' ? (
                                                            <div key={index} className="px-4 py-1 text-gray-400 text-center text-xs border-b border-gray-200">
                                                                {country.label}
                                                            </div>
                                                        ) : (
                                                            <button
                                                                key={index}
                                                                type="button"
                                                                onClick={() => handleCountrySelect(country.value)}
                                                                className={`w-full px-4 py-2 text-left text-sm hover:bg-primary-100 focus:bg-primary-100 focus:outline-none ${
                                                                    formState.country === country.value ? 'bg-primary-100 font-medium' : ''
                                                                } ${country.isPriority ? 'font-medium' : ''}`}
                                                            >
                                                                {country.label}
                                                            </button>
                                                        )
                                                    ))
                                                ) : (
                                                    <div className="px-4 py-3 text-sm text-gray-500 text-center">
                                                        No countries found
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div>
                                <label htmlFor="organisation" className="block text-xs text-main-text mb-1">Organisation Name</label>
                                <input id="organisation" type="text" name="organisation" value={formState.organisation} onChange={handleInputChange} placeholder="Enter organisation name" className="w-full p-2.5 text-sm bg-[#F5F5F5] rounded-md placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-primary-600"/>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                             <div>
                                <label htmlFor="designation" className="block text-xs text-main-text mb-1">Designation</label>
                                <input id="designation" type="text" name="designation" value={formState.designation} onChange={handleInputChange} placeholder="Enter your designation" className="w-full p-2.5 text-sm bg-[#F5F5F5] rounded-md placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-primary-600"/>
                            </div>
                            <div>
                                <label htmlFor="linkedIn" className="block text-xs text-main-text mb-1">LinkedIn or alternative URL <span className="text-gray-400">(Optional)</span></label>
                                <input id="linkedIn" type="url" name="linkedIn" value={formState.linkedIn} onChange={handleInputChange} placeholder="Paste URL here (optional)" className="w-full p-2.5 text-sm bg-[#F5F5F5] rounded-md placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-primary-600"/>
                            </div>
                        </div>

                        {/* Status Messages */}
                        {submitStatus === 'success' && (
                            <InlineAlert variant="success" className="mt-4">
                                Account created successfully! Redirecting…
                            </InlineAlert>
                        )}
                        {submitStatus === 'error' && errorMessage && (
                            <InlineAlert variant="error" className="mt-4">{errorMessage}</InlineAlert>
                        )}
                    </form>
                )}

                <div className="flex items-center gap-4 mt-8 max-w-sm mx-auto">
                    <button 
                        type="button"
                        onClick={() => {
                            if (step === 'otp') {
                                setStep('email');
                                setOtp(['', '', '', '']);
                                setOtpId(null);
                                setTimer(0);
                            } else if (step === 'form') {
                                setStep('otp');
                            } else {
                                trackGTMEvent('signup_back_clicked', {
                                    component: 'SignUpModal',
                                    form_filled: isFormValid
                                });
                                handleModalClose('back');
                            }
                        }}
                        className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-grayscale-1400 bg-white border border-primary-600 rounded-full hover:bg-primary-100 transition-colors shadow-md"
                    >
                        <span className="text-grayscale-1300"><ArrowLeftIcon /></span>
                        Back
                    </button>
                    {step === 'email' && (
                        <button
                            type="button"
                            onClick={handleSendOTP}
                            disabled={emailStatus !== 'available' || isSubmitting}
                            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium rounded-full transition-colors shadow-md disabled:bg-primary-100 disabled:cursor-not-allowed disabled:text-primary-300 bg-primary-600 text-white hover:bg-primary-700"
                        >
                            {isSubmitting ? 'Sending...' : 'Send OTP'}
                            {!isSubmitting && <span className="text-white"><AddIcon /></span>}
                        </button>
                    )}
                    {step === 'otp' && (
                        <button
                            type="button"
                            onClick={handleVerifyOTP}
                            disabled={otp.join('').length !== 4 || isSubmitting}
                            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium rounded-full transition-colors shadow-md disabled:bg-primary-100 disabled:cursor-not-allowed disabled:text-primary-300 bg-primary-600 text-white hover:bg-primary-700"
                        >
                            {isSubmitting ? 'Verifying...' : 'Verify OTP'}
                            {!isSubmitting && <span className="text-white"><AddIcon /></span>}
                        </button>
                    )}
                    {step === 'form' && (
                        <button
                            type="button"
                            onClick={handleSubmit}
                            disabled={!isFormValid || isSubmitting || submitStatus === 'success' || emailStatus === 'exists' || emailStatus === 'moved'}
                            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium rounded-full transition-colors shadow-md disabled:bg-primary-100 disabled:cursor-not-allowed disabled:text-primary-300 bg-primary-600 text-white hover:bg-primary-700"
                        >
                            {isSubmitting ? 'Creating Account...' : 
                             submitStatus === 'success' ? 'Account Created!' : 
                             emailStatus === 'exists' ? 'Email Exists' :
                             'Sign Up'}
                            {!isSubmitting && submitStatus !== 'success' && emailStatus !== 'exists' && <span className="text-white"><AddIcon /></span>}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SignUpModal;

